<script setup lang="ts">
import { FormKitLazyProvider } from '@formkit/vue'
// Overrides @basecom-gmbh/pond/components/ProductListing/Filter/Offcanvas/ProductListingFilterOffcanvas.vue
import type { Schemas } from '@shopware/api-client/api-types';
import { getTranslatedProperty } from '@shopware-pwa/helpers-next';
import type { ListingFilter } from '@basecom-gmbh/pond/types/listing/filter';

const props = withDefaults(
    defineProps<{
        filters: ListingFilter[];
        selectedFilters: Schemas['ProductListingResult']['currentFilters'];
        showResetButton?: boolean;
        productListingStoreKey?: string;
    }>(),
    {
        productListingStoreKey: 'category',
    },
);

const emit = defineEmits<{
    'filter-changed': [key: Schemas['ProductListingResult']['currentFilters']];
    'reset-filters': [];
    'remove-filter': [
        event: {
            code: 'price';
            value: null;
        },
    ];
}>();

const { componentsMappingOffcanvas } = useListingFiltersMapping();
const productListingCriteriaStore = useProductListingCriteriaStore(props.productListingStoreKey);
const { appliedFiltersTotal } = storeToRefs(productListingCriteriaStore);

const sideMenuController = useModal();
const displayedFilter = ref(null);

const onResetClicked = () => {
    if (!displayedFilter.value) {
        emit('reset-filters');
        return;
    }

    emit('filter-changed', { code: displayedFilter.value.code, value: [] });
};
</script>

<template>
    <!-- IDS-36 - Change offcanvas filter, include sorting, add reset-->
    
<FormKitLazyProvider config-file="true">
<FormKit
        type="submit"
        :classes="{
            outer: 'w-full mb-8',
            input: '!w-full flex place-items-center justify-center gap-2 ',
            wrapper: 'w-full',
        }"
        @click="sideMenuController.open()"
    >
        <FormKitIcon
            icon="filter-list"
            class="size-5"
        />
        <span>
            {{ $t('listing.sidebar.title') }}
        </span>
        <UtilityPill
            :number="appliedFiltersTotal"
            styling="light"
            class="!text-sm"
        />
    </FormKit>

    <LayoutSidebar
        v-if="sideMenuController.isOpen"
        :controller="sideMenuController"
        side="right"
        :is-scrollable="false"
        close-button="size-4"
    >
        <template #header>
            <span class="font-extrabold">
                <span v-if="!displayedFilter">
                    {{ $t('listing.sidebar.title') }}
                </span>
                <button
                    v-else
                    class="flex items-center gap-2"
                    @click="displayedFilter = null"
                >
                    <FormKitIcon
                        icon="chevron-left"
                        class="block h-4 w-4"
                    />
                    <template v-if="displayedFilter?.code === 'properties'">
                        {{ getTranslatedProperty(displayedFilter, 'name') }}
                    </template>
                    <template v-else>
                        {{ $t(`listing.sidebar.filter.${displayedFilter.code}.title`) }}
                    </template>
                </button>
            </span>
        </template>
        <template #content>
            <div class="h-full w-full overflow-x-hidden p-0">
                <div
                    class="flex h-full w-full flex-row transition-transform duration-300"
                    :class="{
                        '-translate-x-full': displayedFilter,
                    }"
                >
                    <div class="flex h-full w-full shrink-0 flex-col justify-between">
                        <div class="overflow-y-auto px-6">
                            <ProductListingFilterOffcanvasItem
                                :filter="{ code: 'sorting', label: 'Sorting' }"
                                :selected-filters="selectedFilters"
                                :product-listing-store-key="productListingStoreKey"
                                @click="displayedFilter = { code: 'sorting', label: 'Sorting' }"
                            />

                            <template
                                v-for="filter in props.filters"
                                :key="filter.code"
                            >
                                <ProductListingFilterOffcanvasItem
                                    v-if="componentsMappingOffcanvas[filter.code]"
                                    :filter="filter"
                                    :selected-filters="selectedFilters"
                                    :product-listing-store-key="productListingStoreKey"
                                    @click="displayedFilter = filter"
                                />
                            </template>
                        </div>
                        <div
                            v-if="!displayedFilter"
                            class="m-6 flex gap-2"
                        >
                            <FormKit
                                type="button"
                                :classes="{
                                    outer: 'w-full',
                                    input: 'sm:!w-full !px-4 font-bold',
                                    wrapper: 'w-full',
                                }"
                                :label="$t('productListing.filters.resetFilters')"
                                @click="onResetClicked()"
                            />
                            <FormKit
                                type="submit"
                                :classes="{
                                    outer: 'w-full',
                                    input: 'sm:!w-full !px-4',
                                    wrapper: 'w-full',
                                }"
                                :label="$t('productListing.filters.showResults')"
                                @click="sideMenuController.close()"
                            />
                        </div>
                    </div>
                    <div class="flex h-full w-full shrink-0 flex-col justify-between">
                        <component
                            :is="componentsMappingOffcanvas[displayedFilter?.code]"
                            :filter="displayedFilter"
                            :selected-values="props.selectedFilters"
                            @filter-changed="$event => $emit('filter-changed', $event)"
                            @close="displayedFilter = null"
                            @remove-filter="$emit('remove-filter', $event)"
                        />
                    </div>
                </div>
            </div>
        </template>
        <template #footer />
    </LayoutSidebar>
</FormKitLazyProvider>

</template>
